/* iter-48 — Authors' Portal entrance section.
 *
 * Final design: a quiet "Members' Reading Room" panel whose palette is
 * only a whisper warmer than the app's cream theme — barely separated
 * from the page background, with muted ink-brown accents instead of
 * the heavier sepia variant we tried earlier.
 *
 * App theme reference:
 *   --background: 36 25% 96%   --card: 36 30% 99%
 *   --foreground / --ink: 24 18% 14%
 *   --primary: 28 70% 45%
 *
 * The panel sits 4% darker than the background (36 30% 92%) — enough
 * for separation in the browser, not enough to feel "different".
 */
import React from 'react';
import { KeyRound, BookMarked, ArrowRight } from 'lucide-react';

export default function AuthorsPortalSection({ onOpenPortal, testId = 'authors-portal-section' }) {
  return (
    <section
      data-testid={testId}
      className="mx-auto max-w-6xl px-4 sm:px-6 pb-16 sm:pb-20"
    >
      <div
        className="relative overflow-hidden rounded-xl border p-6"
        style={{
          /* Cream-shifted: a whisper warmer/darker than --background (36 25% 96%) */
          background:
            'linear-gradient(180deg, hsl(36 32% 94%) 0%, hsl(36 28% 91%) 100%)',
          borderColor: 'hsl(30 18% 78%)',
          boxShadow:
            '0 18px 48px -28px hsl(24 18% 14% / 0.18), inset 0 1px 0 hsl(0 0% 100% / 0.6)',
        }}
      >
        {/* Soft library-card inner border — barely-there dashed warm tan */}
        <div
          className="pointer-events-none absolute inset-2 rounded-lg"
          style={{ border: '1px dashed hsl(28 25% 62% / 0.55)' }}
          aria-hidden
        />
        <div className="relative grid grid-cols-1 gap-6 md:grid-cols-[1fr_auto] md:items-center">
          <div>
            <div
              className="mb-3 inline-flex items-center gap-2 rounded-sm border px-2.5 py-1"
              style={{
                borderColor: 'hsl(28 30% 68%)',
                background: 'hsl(36 30% 97% / 0.7)',
                color: 'hsl(24 30% 32%)',
              }}
            >
              <BookMarked className="h-3 w-3" />
              <span className="text-[10.5px] uppercase tracking-[0.22em] font-semibold">
                Members&apos; Reading Room
              </span>
            </div>
            <h2
              className="text-2xl sm:text-3xl font-semibold leading-tight tracking-tight"
              style={{
                color: 'hsl(24 22% 18%)',
                fontFamily: '"Cormorant Garamond","Georgia",serif',
              }}
            >
              The Author&apos;s Portal.
            </h2>
            <p
              className="mt-3 max-w-xl text-[13px] leading-relaxed"
              style={{ color: 'hsl(24 14% 32%)' }}
            >
              A quiet workshop above the bookstore — AI assistance, paid editing,
              cover art, distribution, royalties. Sign your card to enter.
            </p>
          </div>
          <button
            type="button"
            onClick={onOpenPortal}
            data-testid="authors-portal-btn"
            data-testid-alt="authors-portal-cta"
            className="group inline-flex items-center justify-center gap-2 rounded-md border px-6 py-3 text-[13px] font-semibold transition"
            style={{
              borderColor: 'hsl(28 45% 42%)',
              color: 'hsl(36 30% 99%)',
              background:
                'linear-gradient(180deg, hsl(28 65% 48%) 0%, hsl(28 60% 42%) 100%)',
              boxShadow: '0 8px 22px -10px hsl(28 60% 35% / 0.55)',
              fontFamily: '"Cormorant Garamond","Georgia",serif',
              letterSpacing: '0.02em',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.filter = 'brightness(1.05)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.filter = 'brightness(1)'; }}
          >
            <KeyRound className="h-4 w-4" />
            Sign the Card
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </div>
    </section>
  );
}
